import React, { useState, useEffect } from 'react';
import { deliveryBoyAPI } from '../services/api';
import { deliveryManagementAPI } from '../services/deliverySystemAPI';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, 
  MapPin, 
  Phone, 
  Navigation,
  CheckCircle,
  Clock,
  TrendingUp,
  Truck,
  DollarSign,
  Calendar,
  Users,
  Milk,
  AlertTriangle,
  Layers
} from 'lucide-react';
import toast from 'react-hot-toast';
import ItemDetailModal from '../components/ItemDetailModal';

const DeliveryBoyModule = () => {
  const { employee, hasRole } = useAuth();
  const isAdmin = hasRole(['admin', 'super_admin', 'developer']);
  
  const [activeTab, setActiveTab] = useState(isAdmin ? 'overview' : 'myOrders');
  const [deliveryBoyStatusTab, setDeliveryBoyStatusTab] = useState('pending');
  const [myOrders, setMyOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [bottleStats, setBottleStats] = useState(null);
  const [customerBottleBalances, setCustomerBottleBalances] = useState([]);
  const [bottlePageInfo, setBottlePageInfo] = useState({ page: 1, limit: 50, total: 0 });
  const [bottleSearch, setBottleSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [bottleModalOpen, setBottleModalOpen] = useState(false);
  const [currentBottleOrder, setCurrentBottleOrder] = useState(null);
  const [bottleData, setBottleData] = useState({
    bottlesGiven: 0,
    bottlesReturned: 0,
    bottlesBroken: 0,
    notes: ''
  });
  const [selectedCustomerUuid, setSelectedCustomerUuid] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetailModalOpen, setItemDetailModalOpen] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'overview') {
        fetchAdminStats();
      } else if (activeTab === 'deliveryBoys') {
        fetchAllDeliveryBoys();
      } else if (activeTab === 'bottles') {
        fetchCustomerBottleBalances();
      }
    } else {
      if (activeTab === 'myOrders') {
        fetchMyOrders(deliveryBoyStatusTab);
      } else if (activeTab === 'stats') {
        fetchStats();
      }
    }
  }, [activeTab, isAdmin, deliveryBoyStatusTab]);

  const fetchMyOrders = async (status = 'pending') => {
    try {
      setLoading(true);
      const response = await deliveryBoyAPI.getMyOrders({ status });
      setMyOrders(response.data.data);
    } catch (error) {
      console.error('Error fetching my orders:', error);
      toast.error('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await deliveryBoyAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      const response = await deliveryBoyAPI.getAdminStats();
      setAdminStats(response.data.data);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDeliveryBoys = async () => {
    try {
      setLoading(true);
      const response = await deliveryBoyAPI.getAllDeliveryBoys();
      setDeliveryBoys(response.data.data);
    } catch (error) {
      console.error('Error fetching delivery boys:', error);
      toast.error('Failed to load delivery boys');
    } finally {
      setLoading(false);
    }
  };

  const fetchBottleStats = async () => {
    try {
      setLoading(true);
      const response = await deliveryBoyAPI.getBottleStats();
      setBottleStats(response.data.data);
    } catch (error) {
      console.error('Error fetching bottle stats:', error);
      toast.error('Failed to load bottle statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerBottleBalances = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await deliveryBoyAPI.getCustomerBottleBalances({
        page,
        limit: 50,
        search
      });
      setCustomerBottleBalances(response.data.data.balances || []);
      setBottlePageInfo(response.data.data.pagination || {});
    } catch (error) {
      console.error('Error fetching customer bottle balances:', error);
      toast.error('Failed to load customer bottle data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderUuid, newStatus) => {
    try {
      await deliveryBoyAPI.updateOrderStatus(orderUuid, newStatus);
      toast.success('Order status updated successfully!');
      fetchMyOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleCompleteDelivery = async (orderUuid, notes) => {
    try {
      await deliveryBoyAPI.completeDelivery(orderUuid, notes);
      toast.success('Delivery completed successfully!');
      setSelectedOrder(null);
      fetchMyOrders();
    } catch (error) {
      console.error('Error completing delivery:', error);
      const errorMessage = error.response?.data?.message || 'Failed to complete delivery';
      
      if (errorMessage.includes('Bottle tracking is required')) {
        toast.error('⚠️ Please track bottles before marking as delivered');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const openBottleModal = (order) => {
    setCurrentBottleOrder(order);
    setBottleData({
      bottlesGiven: order.orderBottleData?.bottlesGiven || 0,
      bottlesReturned: order.orderBottleData?.bottlesReturned || 0,
      bottlesBroken: order.orderBottleData?.bottlesBroken || 0,
      notes: order.orderBottleData?.bottleNotes || ''
    });
    setBottleModalOpen(true);
  };

  const handleBottleUpdate = async () => {
    if (!currentBottleOrder) return;

    try {
      const previousBalance = currentBottleOrder.bottleBalance?.previousBalance || 0;
      const netChange = bottleData.bottlesGiven - bottleData.bottlesReturned - bottleData.bottlesBroken;
      const newBalance = previousBalance + netChange;
      const charge = bottleData.bottlesBroken * 50;

      const bottlePayload = {
        bottles500mlGiven: bottleData.bottlesGiven || 0,
        bottles500mlReturned: bottleData.bottlesReturned || 0,
        bottles500mlBroken: bottleData.bottlesBroken || 0,
        bottles1LGiven: 0,
        bottles1LReturned: 0,
        bottles1LBroken: 0,
        notes: bottleData.notes || ''
      };

      await deliveryBoyAPI.updateBottles(currentBottleOrder.uuid, bottlePayload);
      
      let message = `✓ Bottle information updated. New balance: ${newBalance} bottles`;
      if (charge > 0) {
        message += `. Charge: ₹${charge}`;
      }
      
      toast.success(message);
      setBottleModalOpen(false);
      setCurrentBottleOrder(null);
      fetchMyOrders();
    } catch (error) {
      console.error('Error updating bottles:', error);
      toast.error('Failed to update bottle information');
    }
  };

  const openNavigation = (latitude, longitude) => {
    if (latitude && longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(url, '_blank');
    } else {
      toast.error('Location coordinates not available');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3 sm:px-6">
        <h1 className="text-xl font-bold text-gray-900 truncate">
          {isAdmin ? '📊 Delivery Management' : '🚚 My Deliveries'}
        </h1>
      </div>

      {/* Tab Navigation - Mobile Optimized */}
      <div className="sticky top-[56px] z-30 bg-white border-b border-gray-200">
        <div className="flex overflow-x-auto scrollbar-hide">
          {isAdmin ? (
            <>
              <MobileTab 
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
                icon={<TrendingUp className="w-4 h-4" />}
                label="Overview"
              />
              <MobileTab 
                active={activeTab === 'deliveryBoys'}
                onClick={() => setActiveTab('deliveryBoys')}
                icon={<Users className="w-4 h-4" />}
                label="Boys"
              />
              <MobileTab 
                active={activeTab === 'bottles'}
                onClick={() => setActiveTab('bottles')}
                icon={<Milk className="w-4 h-4" />}
                label="Bottles"
              />
            </>
          ) : (
            <>
              <MobileTab 
                active={activeTab === 'myOrders'}
                onClick={() => setActiveTab('myOrders')}
                icon={<Truck className="w-4 h-4" />}
                label="Orders"
              />
              <MobileTab 
                active={activeTab === 'stats'}
                onClick={() => setActiveTab('stats')}
                icon={<TrendingUp className="w-4 h-4" />}
                label="Stats"
              />
            </>
          )}
        </div>
      </div>

      {/* Status Filter Tabs for Delivery Boys */}
      {!isAdmin && activeTab === 'myOrders' && (
        <div className="sticky top-[108px] z-25 bg-white border-b border-gray-200">
          <div className="flex px-4">
            <button
              onClick={() => setDeliveryBoyStatusTab('pending')}
              className={`flex-1 py-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                deliveryBoyStatusTab === 'pending'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              ⏳ Pending
            </button>
            <button
              onClick={() => setDeliveryBoyStatusTab('delivered')}
              className={`flex-1 py-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                deliveryBoyStatusTab === 'delivered'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              ✓ Delivered
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="pb-6">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="px-4 py-4 sm:px-6">
            {isAdmin ? (
              <>
                {activeTab === 'overview' && adminStats && (
                  <AdminOverviewTab stats={adminStats} />
                )}
                {activeTab === 'deliveryBoys' && (
                  <DeliveryBoysTab deliveryBoys={deliveryBoys} />
                )}
                {activeTab === 'bottles' && (
                  <CustomerBottleTrackingTab 
                    customers={customerBottleBalances}
                    pageInfo={bottlePageInfo}
                    search={bottleSearch}
                    onSearchChange={setBottleSearch}
                    onPageChange={(page) => fetchCustomerBottleBalances(page, bottleSearch)}
                    onSearch={() => fetchCustomerBottleBalances(1, bottleSearch)}
                    selectedCustomerUuid={selectedCustomerUuid}
                    onSelectCustomer={setSelectedCustomerUuid}
                  />
                )}
              </>
            ) : (
              <>
                {activeTab === 'myOrders' && (
                  <MyOrdersTab
                    orders={myOrders}
                    onStatusUpdate={handleStatusUpdate}
                    onComplete={handleCompleteDelivery}
                    onNavigate={openNavigation}
                    selectedOrder={selectedOrder}
                    setSelectedOrder={setSelectedOrder}
                    onBottleModal={openBottleModal}
                  />
                )}
                {activeTab === 'stats' && stats && (
                  <StatsTab stats={stats} />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Bottle Tracking Modal */}
      <BottleTrackingModal
        isOpen={bottleModalOpen}
        order={currentBottleOrder}
        bottleData={bottleData}
        onClose={() => setBottleModalOpen(false)}
        onUpdate={handleBottleUpdate}
        onDataChange={setBottleData}
      />

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={itemDetailModalOpen}
        item={selectedItem}
        onClose={() => setItemDetailModalOpen(false)}
      />
    </div>
  );
};

// Mobile Tab Component
const MobileTab = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 border-b-2 transition-colors font-medium text-sm whitespace-nowrap ${
      active
        ? 'border-blue-600 text-blue-600 bg-blue-50'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);

// My Orders Tab
const MyOrdersTab = ({ orders, onStatusUpdate, onComplete, onNavigate, selectedOrder, setSelectedOrder, onBottleModal }) => {
  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">No orders assigned to you</p>
        <p className="text-gray-500 text-sm mt-2">Assign orders from the Available Orders tab</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <MyOrderCard
          key={order.uuid}
          order={order}
          onStatusUpdate={onStatusUpdate}
          onComplete={onComplete}
          onNavigate={onNavigate}
          isSelected={selectedOrder === order.uuid}
          onSelect={() => setSelectedOrder(selectedOrder === order.uuid ? null : order.uuid)}
          onBottleModal={onBottleModal}
        />
      ))}
    </div>
  );
};

// My Order Card Component - Mobile Optimized
const MyOrderCard = ({ order, onStatusUpdate, onComplete, onNavigate, isSelected, onSelect, onBottleModal }) => {
  const [notes, setNotes] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const handleComplete = () => {
    onComplete(order.uuid, notes);
    setShowCompleteModal(false);
    setNotes('');
  };

  const bottlesTracked = order.orderBottleData && (
    order.orderBottleData.bottlesGiven > 0 || 
    order.orderBottleData.bottlesReturned > 0 || 
    order.orderBottleData.bottlesBroken > 0
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-3 shadow-sm">
      {/* Order Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex justify-between items-start gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 truncate">{order.orderNumber}</h3>
            <p className="text-sm text-gray-600 truncate">{order.customerName}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xl font-bold text-gray-900">₹{order.totalAmount}</p>
            <p className={`text-xs font-medium mt-0.5 ${
              order.paymentStatus === 'completed' ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {order.paymentStatus === 'completed' ? '✓ Paid' : '⏳ Pending'}
            </p>
          </div>
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
          <div className="flex items-center gap-1 text-gray-600">
            <Clock className="w-3 h-3" />
            <span>{order.deliveryTimeSlot}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <Calendar className="w-3 h-3" />
            <span>{new Date(order.deliveryDate).toLocaleDateString('en-IN')}</span>
          </div>
          <a href={`tel:${order.customerPhone}`} className="flex items-center gap-1 text-blue-600 font-medium col-span-2">
            <Phone className="w-3 h-3" />
            <span>{order.customerPhone}</span>
          </a>
        </div>

        {/* Address */}
        <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
          <p className="font-medium text-gray-700 mb-1">📍 {order.deliveryAddress}</p>
          {order.deliveryLandmark && <p className="text-gray-500">{order.deliveryLandmark}</p>}
          <p className="text-gray-500">{order.deliveryCity} - {order.deliveryPincode}</p>
        </div>
      </div>

      {/* Bottle Balance Alert */}
      {order.bottleBalance && order.bottleBalance.previousBalance > 0 && (
        <div className={`px-4 py-2 border-l-4 text-xs ${
          order.bottleBalance.isHighRisk 
            ? 'bg-red-50 border-red-400 text-red-700' 
            : 'bg-yellow-50 border-yellow-400 text-yellow-700'
        }`}>
          <p className="font-bold">🥛 {order.bottleBalance.previousBalance} bottles pending</p>
          {order.bottleBalance.isHighRisk && <p>⚠️ HIGH RISK</p>}
          {order.bottleBalance.totalCharges > 0 && (
            <p className="text-xs">Charges: ₹{order.bottleBalance.totalCharges}</p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-4 py-3 space-y-2">
        {order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'returned' && (
          <>
            <button
              onClick={() => onBottleModal(order)}
              className="w-full py-2.5 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              <Milk className="w-4 h-4" />
              Track Bottles
            </button>

            {bottlesTracked ? (
              <button
                onClick={() => setShowCompleteModal(true)}
                className="w-full py-2.5 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Complete Delivery
              </button>
            ) : (
              <div className="w-full py-2.5 bg-gray-300 text-gray-600 rounded-lg font-medium text-sm flex items-center justify-center gap-2 cursor-not-allowed">
                <AlertTriangle className="w-4 h-4" />
                Track Bottles First
              </div>
            )}
          </>
        )}

        {order.status === 'delivered' && (
          <div className="w-full py-2.5 bg-green-50 border-2 border-green-600 text-green-700 rounded-lg font-bold text-sm flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Delivered on {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('en-IN') : 'Unknown date'}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {order.deliveryLatitude && order.deliveryLongitude && order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'returned' && (
            <button
              onClick={() => onNavigate(order.deliveryLatitude, order.deliveryLongitude)}
              className="py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
            >
              <Navigation className="w-4 h-4" />
              <span className="hidden xs:inline">Navigate</span>
            </button>
          )}
          <button
            onClick={onSelect}
            className="py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
          >
            {isSelected ? '▲ Hide' : '▼ Items'}
          </button>
        </div>
      </div>

      {/* Order Items - Collapsible */}
      {isSelected && order.items && order.items.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50 p-3">
          <div className="space-y-2">
            {order.items.map((item) => (
              <button
                key={item.uuid}
                onClick={() => {
                  setSelectedItem(item);
                  setItemDetailModalOpen(true);
                }}
                className="w-full flex gap-2 bg-white p-2 rounded text-sm hover:shadow-md hover:bg-gray-50 transition-all cursor-pointer text-left"
              >
                {item.productImage && (
                  <img src={item.productImage} alt={item.productName} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1">
                    <p className="font-medium text-gray-900 text-sm truncate">{item.productName}</p>
                    {item.variant && <span className="text-xs font-semibold text-blue-600 flex-shrink-0">{item.variant}</span>}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Qty: <span className="font-semibold">{item.quantity}</span></p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Complete Delivery Modal - Mobile Full Screen */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-4 w-full sm:max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Complete Delivery</h3>
            <p className="text-sm text-gray-600 mb-4">Add notes (optional):</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Delivered, left at door, customer unavailable..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm mb-4"
              rows="4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Bottle Tracking Modal Component - Mobile Optimized
const BottleTrackingModal = ({ isOpen, order, bottleData, onClose, onUpdate, onDataChange }) => {
  if (!isOpen || !order) return null;

  const brokenCharge = bottleData.bottlesBroken * 50;
  const previousBalance = order.bottleBalance?.previousBalance || 0;
  const netChange = bottleData.bottlesGiven - bottleData.bottlesReturned - bottleData.bottlesBroken;
  const newBalance = previousBalance + netChange;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-lg w-full sm:max-w-md max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="text-lg font-bold text-gray-900">🥛 Track Bottles</h3>
            <p className="text-xs text-gray-600 mt-0.5">{order.customerName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Warning Banner */}
          <div className="p-3 bg-orange-50 border-l-4 border-orange-500 rounded text-sm">
            <p className="font-bold text-orange-900">⚠️ Required before delivery</p>
            <p className="text-orange-700 text-xs mt-1">Track bottles to complete this order</p>
          </div>

          {/* Balance Overview - Compact */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-bold text-blue-900 mb-2">Current Balance</p>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="text-center">
                <p className="text-blue-600 font-bold text-sm">{order.bottleBalance?.totalGiven || 0}</p>
                <p className="text-blue-700">Given</p>
              </div>
              <div className="text-center">
                <p className="text-blue-600 font-bold text-sm">{order.bottleBalance?.totalReturned || 0}</p>
                <p className="text-blue-700">Returned</p>
              </div>
              <div className="text-center">
                <p className="text-blue-600 font-bold text-sm">{order.bottleBalance?.totalBroken || 0}</p>
                <p className="text-blue-700">Broken</p>
              </div>
              <div className="text-center bg-white rounded border border-blue-200 p-1">
                <p className={`font-bold text-sm ${previousBalance > 10 ? 'text-red-600' : 'text-green-600'}`}>
                  {previousBalance}
                </p>
                <p className="text-gray-700 text-xs">Holding</p>
              </div>
            </div>
          </div>

          {/* Input Fields */}
          <div className="space-y-3">
            {/* Bottles Given */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">📦 Given Today</label>
              <input
                type="number"
                min="0"
                value={bottleData.bottlesGiven}
                onChange={(e) => onDataChange({ ...bottleData, bottlesGiven: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg font-bold"
                placeholder="0"
              />
            </div>

            {/* Bottles Returned */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">🔄 Returned Today</label>
              <input
                type="number"
                min="0"
                value={bottleData.bottlesReturned}
                onChange={(e) => onDataChange({ ...bottleData, bottlesReturned: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg font-bold"
                placeholder="0"
              />
            </div>

            {/* Bottles Broken */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">💔 Broken/Damaged</label>
              <input
                type="number"
                min="0"
                value={bottleData.bottlesBroken}
                onChange={(e) => onDataChange({ ...bottleData, bottlesBroken: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg font-bold"
                placeholder="0"
              />
              {brokenCharge > 0 && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                  <p className="text-red-700 font-bold">Charge: ₹{brokenCharge} (₹50/bottle)</p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">📝 Notes (Optional)</label>
              <textarea
                value={bottleData.notes}
                onChange={(e) => onDataChange({ ...bottleData, notes: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder="Any issues with bottles..."
              />
            </div>
          </div>

          {/* New Balance Summary */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-xs font-bold text-purple-900 mb-2">New Balance</p>
            <div className="space-y-1 text-xs mb-2">
              {previousBalance > 0 && <div className="flex justify-between text-gray-700"><span>Start:</span><span>{previousBalance}</span></div>}
              {bottleData.bottlesGiven > 0 && <div className="flex justify-between text-green-700"><span>+ Given:</span><span>+{bottleData.bottlesGiven}</span></div>}
              {bottleData.bottlesReturned > 0 && <div className="flex justify-between text-blue-700"><span>- Returned:</span><span>-{bottleData.bottlesReturned}</span></div>}
              {bottleData.bottlesBroken > 0 && <div className="flex justify-between text-red-700"><span>- Broken:</span><span>-{bottleData.bottlesBroken}</span></div>}
            </div>
            <div className="border-t border-purple-300 pt-2 flex justify-between">
              <span className="font-bold text-purple-900">Total:</span>
              <span className={`font-bold text-lg ${newBalance > 10 ? 'text-red-600' : 'text-green-600'}`}>
                {newBalance} 🥛
              </span>
            </div>
            {brokenCharge > 0 && (
              <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700 font-bold">
                Outstanding: ₹{brokenCharge}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onUpdate}
            className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
          >
            Save & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Stats Tab
const StatsTab = ({ stats }) => {
  const today = stats?.today || {};
  const thisWeek = stats?.thisWeek || {};
  const thisMonth = stats?.thisMonth || {};

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-3">📅 Today</h3>
        <div className="grid grid-cols-3 gap-2">
          <StatCard title="Orders" value={today.total || 0} icon={<Package className="w-5 h-5" />} color="blue" compact />
          <StatCard title="Done" value={today.delivered || 0} icon={<CheckCircle className="w-5 h-5" />} color="green" compact />
          <StatCard title="Pending" value={today.pending || 0} icon={<Clock className="w-5 h-5" />} color="yellow" compact />
        </div>
      </div>

      <div>
        <h3 className="text-base font-bold text-gray-900 mb-3">📊 This Week</h3>
        <div className="grid grid-cols-3 gap-2">
          <StatCard title="Orders" value={thisWeek.total || 0} icon={<Package className="w-5 h-5" />} color="blue" compact />
          <StatCard title="Done" value={thisWeek.delivered || 0} icon={<CheckCircle className="w-5 h-5" />} color="green" compact />
          <StatCard title="Pending" value={thisWeek.pending || 0} icon={<Clock className="w-5 h-5" />} color="yellow" compact />
        </div>
      </div>

      <div>
        <h3 className="text-base font-bold text-gray-900 mb-3">📈 This Month</h3>
        <div className="grid grid-cols-3 gap-2">
          <StatCard title="Orders" value={thisMonth.total || 0} icon={<Package className="w-5 h-5" />} color="blue" compact />
          <StatCard title="Done" value={thisMonth.delivered || 0} icon={<CheckCircle className="w-5 h-5" />} color="green" compact />
          <StatCard title="Pending" value={thisMonth.pending || 0} icon={<Clock className="w-5 h-5" />} color="yellow" compact />
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, compact }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    indigo: 'bg-indigo-500',
    yellow: 'bg-yellow-500'
  };

  if (compact) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-3 text-center border border-gray-100">
        <div className={`${colorClasses[color]} text-white p-2 rounded-lg mx-auto mb-2 w-fit`}>
          {icon}
        </div>
        <p className="text-xs text-gray-600 font-medium mb-1">{title}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${colorClasses[color]} text-white p-3 rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Admin Overview Tab - Shows overall delivery statistics
const AdminOverviewTab = ({ stats }) => {
  const overview = stats?.overview || {};
  const today = stats?.today || {};
  const thisWeek = stats?.thisWeek || {};
  const thisMonth = stats?.thisMonth || {};

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Team Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Total Delivery Boys"
            value={overview.totalDeliveryBoys || 0}
            icon={<Users className="w-6 h-6" />}
            color="indigo"
          />
          <StatCard
            title="Available"
            value={overview.availableDeliveryBoys || 0}
            icon={<CheckCircle className="w-6 h-6" />}
            color="green"
          />
          <StatCard
            title="Busy"
            value={overview.busyDeliveryBoys || 0}
            icon={<Truck className="w-6 h-6" />}
            color="orange"
          />
        </div>
      </div>

      {/* Today's Overall Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Delivery Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Deliveries"
            value={today.totalDeliveries || 0}
            icon={<Package className="w-6 h-6" />}
            color="blue"
          />
          <StatCard
            title="Completed"
            value={today.completedDeliveries || 0}
            icon={<CheckCircle className="w-6 h-6" />}
            color="green"
          />
          <StatCard
            title="Pending"
            value={today.pendingDeliveries || 0}
            icon={<Clock className="w-6 h-6" />}
            color="orange"
          />
          <StatCard
            title="Revenue"
            value={`₹${(today.totalRevenue || 0).toLocaleString()}`}
            icon={<DollarSign className="w-6 h-6" />}
            color="purple"
          />
        </div>
      </div>

      {/* This Week's Overall Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">This Week's Delivery Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Deliveries"
            value={thisWeek.totalDeliveries || 0}
            icon={<Package className="w-6 h-6" />}
            color="blue"
          />
          <StatCard
            title="Completed"
            value={thisWeek.completedDeliveries || 0}
            icon={<CheckCircle className="w-6 h-6" />}
            color="green"
          />
          <StatCard
            title="Pending"
            value={thisWeek.pendingDeliveries || 0}
            icon={<Clock className="w-6 h-6" />}
            color="orange"
          />
          <StatCard
            title="Revenue"
            value={`₹${(thisWeek.totalRevenue || 0).toLocaleString()}`}
            icon={<DollarSign className="w-6 h-6" />}
            color="purple"
          />
        </div>
      </div>

      {/* This Month's Overall Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">This Month's Delivery Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Deliveries"
            value={thisMonth.totalDeliveries || 0}
            icon={<Package className="w-6 h-6" />}
            color="blue"
          />
          <StatCard
            title="Completed"
            value={thisMonth.completedDeliveries || 0}
            icon={<CheckCircle className="w-6 h-6" />}
            color="green"
          />
          <StatCard
            title="Pending"
            value={thisMonth.pendingDeliveries || 0}
            icon={<Clock className="w-6 h-6" />}
            color="orange"
          />
          <StatCard
            title="Revenue"
            value={`₹${(thisMonth.totalRevenue || 0).toLocaleString()}`}
            icon={<DollarSign className="w-6 h-6" />}
            color="purple"
          />
        </div>
      </div>
    </div>
  );
};

// Delivery Boys Tab - Shows list of all delivery boys with their individual stats
const DeliveryBoysTab = ({ deliveryBoys }) => {
  const [assignedGroups, setAssignedGroups] = useState({});
  const [groupsLoading, setGroupsLoading] = useState(false);

  useEffect(() => {
    const getTodayDate = () => {
      const today = new Date();
      return today.toISOString().split('T')[0];
    };

    const fetchGroupsForDeliveryBoys = async () => {
      if (!deliveryBoys || deliveryBoys.length === 0) return;

      try {
        setGroupsLoading(true);
        const today = getTodayDate();
        
        const response = await deliveryManagementAPI.getGroupsByDate(today);
        const groups = response.data.data || [];

        const groupsByDeliveryBoy = {};
        groups.forEach((group) => {
          const deliveryBoyUuid = group.assigned_delivery_boy_uuid || group.assignedDeliveryBoyUuid;
          if (deliveryBoyUuid) {
            if (!groupsByDeliveryBoy[deliveryBoyUuid]) {
              groupsByDeliveryBoy[deliveryBoyUuid] = [];
            }
            groupsByDeliveryBoy[deliveryBoyUuid].push(group);
          }
        });

        setAssignedGroups(groupsByDeliveryBoy);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroupsForDeliveryBoys();
  }, [deliveryBoys]);

  if (!deliveryBoys || deliveryBoys.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No delivery boys found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">All Delivery Boys</h3>
        <p className="text-sm text-gray-600">{deliveryBoys.length} delivery boy(s)</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {deliveryBoys.map((deliveryBoy) => (
          <div key={deliveryBoy.uuid} className="bg-white rounded-lg shadow-md p-6">
            {/* Delivery Boy Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {deliveryBoy.name?.charAt(0).toUpperCase() || 'D'}
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900">{deliveryBoy.name}</h4>
                  <p className="text-sm text-gray-600">{deliveryBoy.email}</p>
                  <p className="text-sm text-gray-600">{deliveryBoy.phone}</p>
                  {deliveryBoy.deliveryAreas && (
                    <p className="text-xs text-blue-600 mt-1">
                      Areas: {deliveryBoy.deliveryAreas}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    deliveryBoy.isAvailable
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {deliveryBoy.isAvailable ? 'Available' : 'Busy'}
                </span>
                {deliveryBoy.vehicleType && (
                  <span className="text-xs text-gray-600">
                    🚗 {deliveryBoy.vehicleType}
                  </span>
                )}
              </div>
            </div>

            {/* Today's Stats */}
            <div className="mb-4">
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Today's Performance</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium">Total</p>
                  <p className="text-lg font-bold text-blue-900">
                    {deliveryBoy.stats?.today?.totalDeliveries || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-600 font-medium">Completed</p>
                  <p className="text-lg font-bold text-green-900">
                    {deliveryBoy.stats?.today?.completedDeliveries || 0}
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs text-orange-600 font-medium">Pending</p>
                  <p className="text-lg font-bold text-orange-900">
                    {deliveryBoy.stats?.today?.pendingDeliveries || 0}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-medium">Revenue</p>
                  <p className="text-lg font-bold text-purple-900">
                    ₹{(deliveryBoy.stats?.today?.totalRevenue || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Week & Month Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* This Week */}
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">This Week</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-600">Deliveries</p>
                    <p className="text-sm font-bold text-gray-900">
                      {deliveryBoy.stats?.thisWeek?.totalDeliveries || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-600">Revenue</p>
                    <p className="text-sm font-bold text-gray-900">
                      ₹{(deliveryBoy.stats?.thisWeek?.totalRevenue || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* This Month */}
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">This Month</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-600">Deliveries</p>
                    <p className="text-sm font-bold text-gray-900">
                      {deliveryBoy.stats?.thisMonth?.totalDeliveries || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-600">Revenue</p>
                    <p className="text-sm font-bold text-gray-900">
                      ₹{(deliveryBoy.stats?.thisMonth?.totalRevenue || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Assigned Groups Today */}
            {assignedGroups[deliveryBoy.uuid] && assignedGroups[deliveryBoy.uuid].length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  Assigned Groups Today ({assignedGroups[deliveryBoy.uuid].length})
                </h5>
                <div className="space-y-2">
                  {assignedGroups[deliveryBoy.uuid].map((group) => (
                    <div key={group.id || group.uuid} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm">{group.group_name || group.groupName}</p>
                          <p className="text-xs text-gray-600 mt-1">
                            📍 {group.total_orders || group.totalOrders || 0} orders • 
                            🎯 {(group.radius_km || group.radiusKm || 0).toFixed(1)}km radius
                          </p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          group.status === 'assigned' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {group.status || 'assigned'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Groups Assigned */}
            {(!assignedGroups[deliveryBoy.uuid] || assignedGroups[deliveryBoy.uuid].length === 0) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-gray-400" />
                  No groups assigned for today
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Bottle Analytics Tab
const BottleAnalyticsTab = ({ stats }) => {
  if (!stats) return null;

  const {
    totalBottles = {},
    pendingCharges = {},
    deliveryBoys = []
  } = stats;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Issued</p>
              <p className="text-3xl font-bold text-blue-900">{totalBottles.totalIssued || 0}</p>
            </div>
            <Milk className="w-12 h-12 text-blue-300" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Total Returned</p>
              <p className="text-3xl font-bold text-green-900">{totalBottles.totalReturned || 0}</p>
            </div>
            <Milk className="w-12 h-12 text-green-300" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Total Broken</p>
              <p className="text-3xl font-bold text-red-900">{totalBottles.totalBroken || 0}</p>
            </div>
            <AlertTriangle className="w-12 h-12 text-red-300" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 font-medium">Outstanding Charges</p>
              <p className="text-3xl font-bold text-amber-900">₹{pendingCharges.totalAmount?.toLocaleString() || 0}</p>
              <p className="text-xs text-amber-600 mt-1">{pendingCharges.count || 0} pending</p>
            </div>
            <DollarSign className="w-12 h-12 text-amber-300" />
          </div>
        </div>
      </div>

      {/* Current Outstanding */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Outstanding</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-700 font-medium">Bottles in Circulation</p>
            <p className="text-2xl font-bold text-yellow-900 mt-2">
              {((totalBottles.totalIssued || 0) - (totalBottles.totalReturned || 0) - (totalBottles.totalBroken || 0))}
            </p>
            <p className="text-xs text-yellow-600 mt-1">Customers currently holding</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700 font-medium">Breakage Rate</p>
            <p className="text-2xl font-bold text-red-900 mt-2">
              {totalBottles.totalIssued > 0 
                ? ((totalBottles.totalBroken / totalBottles.totalIssued) * 100).toFixed(1) 
                : 0}%
            </p>
            <p className="text-xs text-red-600 mt-1">Of issued bottles</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 font-medium">Recovery Rate</p>
            <p className="text-2xl font-bold text-blue-900 mt-2">
              {totalBottles.totalIssued > 0 
                ? (((totalBottles.totalReturned + totalBottles.totalBroken) / totalBottles.totalIssued) * 100).toFixed(1) 
                : 0}%
            </p>
            <p className="text-xs text-blue-600 mt-1">Returned + broken</p>
          </div>
        </div>
      </div>

      {/* Delivery Boy Statistics */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Boy Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Delivery Boy</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Orders</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Given</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Returned</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Broken</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Charges</th>
              </tr>
            </thead>
            <tbody>
              {deliveryBoys && deliveryBoys.length > 0 ? (
                deliveryBoys.map((boy) => (
                  <tr key={boy.uuid} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{boy.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{boy.ordersWithBottles || 0}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-medium">{boy.totalGiven || 0}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 font-medium">{boy.totalReturned || 0}</td>
                    <td className="px-4 py-3 text-sm text-red-600 font-medium">{boy.totalBroken || 0}</td>
                    <td className="px-4 py-3 text-sm font-medium">
                      <span className={boy.totalCharges > 0 ? 'text-red-600' : 'text-gray-600'}>
                        ₹{(boy.totalCharges || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-3 text-center text-gray-500">
                    No delivery boy data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Customer Bottle Tracking Tab - For Admin Management
const CustomerBottleTrackingTab = ({ 
  customers, 
  pageInfo, 
  search, 
  onSearchChange, 
  onSearch,
  onPageChange,
  selectedCustomerUuid,
  onSelectCustomer
}) => {
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustData, setAdjustData] = useState({
    bottleSize: '500ml',
    quantity: 0,
    adjustmentType: 'add',
    reason: ''
  });

  const fetchCustomerDetails = async (customerUuid) => {
    try {
      const response = await deliveryBoyAPI.getCustomerBottleDetail(customerUuid);
      setCustomerDetails(response.data.data);
      
      const transResponse = await deliveryBoyAPI.getCustomerBottleTransactions(customerUuid, { limit: 20 });
      setTransactions(transResponse.data.data.transactions || []);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    }
  };

  const handleAdjustBalance = async () => {
    if (!customerDetails || !adjustData.reason.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await deliveryBoyAPI.adjustCustomerBottleBalance({
        customerUuid: customerDetails.customer.uuid,
        ...adjustData
      });
      toast.success('Balance adjusted successfully');
      setAdjustModalOpen(false);
      setAdjustData({ bottleSize: '500ml', quantity: 0, adjustmentType: 'add', reason: '' });
      fetchCustomerDetails(customerDetails.customer.uuid);
    } catch (error) {
      console.error('Error adjusting balance:', error);
      toast.error('Failed to adjust balance');
    }
  };

  if (customers.length === 0) {
    return (
      <div className="text-center py-12">
        <Milk className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No customers with pending bottles</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && onSearch()}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
        <button
          onClick={onSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
        >
          🔍 Search
        </button>
      </div>

      {/* Customer List */}
      <div className="space-y-3">
        {customers.map((customer) => (
          <div key={customer.customer_uuid} className="bg-white rounded-lg border border-gray-200 shadow-sm">
            {/* Customer Header */}
            <button
              onClick={() => {
                setExpandedCustomer(expandedCustomer === customer.customer_uuid ? null : customer.customer_uuid);
                if (expandedCustomer !== customer.customer_uuid) {
                  fetchCustomerDetails(customer.customer_uuid);
                }
              }}
              className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 truncate">{customer.customer_name}</h3>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <a href={`tel:${customer.customer_phone}`} className="text-blue-600 hover:underline">
                        {customer.customer_phone}
                      </a>
                    </div>
                    <div className="truncate">{customer.customer_email}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                    <p className="text-xs text-yellow-700 font-medium">Holding</p>
                    <p className="text-lg font-bold text-yellow-900">{customer.total_pending_bottles || 0}</p>
                  </div>
                </div>
              </div>
            </button>

            {/* Expanded Details */}
            {expandedCustomer === customer.customer_uuid && customerDetails && customerDetails.customer.uuid === customer.customer_uuid && (
              <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                {/* Bottle Balance Details */}
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-sm">🥛 Bottle Balance by Size</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {customerDetails.balances.map((balance) => (
                      <div key={balance.bottle_size} className="bg-white p-3 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 font-medium">{balance.bottle_size}</p>
                        <div className="mt-1 space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Given:</span>
                            <span className="font-bold text-green-600">{balance.total_given}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Returned:</span>
                            <span className="font-bold text-blue-600">{balance.total_collected}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Broken:</span>
                            <span className="font-bold text-red-600">{balance.total_broken}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-1 flex justify-between font-bold">
                            <span>Pending:</span>
                            <span className={balance.pending_bottles > 10 ? 'text-red-600' : 'text-gray-900'}>
                              {balance.pending_bottles}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Transactions */}
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 text-sm">📜 Recent Transactions</h4>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {transactions.length > 0 ? (
                      transactions.map((txn, idx) => (
                        <div key={idx} className="bg-white p-2 rounded text-xs border border-gray-200">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-gray-900">{txn.transaction_type.toUpperCase()}</span>
                            <span className="text-gray-600">{new Date(txn.transaction_date).toLocaleDateString('en-IN')}</span>
                          </div>
                          <p className="text-gray-600">{txn.quantity}x {txn.bottle_size}</p>
                          {txn.delivery_boy_name && <p className="text-gray-500">by {txn.delivery_boy_name}</p>}
                          {txn.notes && <p className="text-gray-500 italic">{txn.notes}</p>}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">No transactions</p>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => setAdjustModalOpen(true)}
                  className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                >
                  ⚙️ Adjust Balance
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pageInfo.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {pageInfo.page > 1 && (
            <button
              onClick={() => onPageChange(pageInfo.page - 1)}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              ← Previous
            </button>
          )}
          <div className="flex items-center gap-1">
            {Array.from({ length: pageInfo.totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  pageInfo.page === page
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          {pageInfo.page < pageInfo.totalPages && (
            <button
              onClick={() => onPageChange(pageInfo.page + 1)}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              Next →
            </button>
          )}
        </div>
      )}

      {/* Adjust Balance Modal */}
      {adjustModalOpen && customerDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg w-full sm:max-w-md p-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Adjust Bottle Balance</h3>
            <p className="text-sm text-gray-600 mb-4">{customerDetails.customer.name}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Bottle Size</label>
                <select
                  value={adjustData.bottleSize}
                  onChange={(e) => setAdjustData({ ...adjustData, bottleSize: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                >
                  <option value="500ml">500ml</option>
                  <option value="1L">1L</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Action</label>
                <select
                  value={adjustData.adjustmentType}
                  onChange={(e) => setAdjustData({ ...adjustData, adjustmentType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                >
                  <option value="add">Add Bottles</option>
                  <option value="subtract">Remove Bottles</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={adjustData.quantity}
                  onChange={(e) => setAdjustData({ ...adjustData, quantity: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg font-bold"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Reason</label>
                <textarea
                  value={adjustData.reason}
                  onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="e.g., Manual correction, customer complaint, etc."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setAdjustModalOpen(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustBalance}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
              >
                Save Adjustment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryBoyModule;